import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  FaClipboardCheck, 
  FaFilter, 
  FaEye, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaExclamationCircle, 
  FaShoppingCart,
  FaUser,
  FaCalendarAlt,
  FaFileAlt,
  FaBuilding,
  FaEdit,
  FaPercent,
  FaDollarSign,
  FaAngleDown,
  FaTag,
  FaUndo,
  FaSave,
  FaSyncAlt,
  FaSearch,
  FaInfoCircle,
  FaTrash,
  FaTachometerAlt
} from 'react-icons/fa';
import supabase from '../../supabaseClient';
import '../../styles/order-page.css';

// Hook para monitoramento de performance otimizado
const usePerformanceMonitor = () => {
  const [metrics, setMetrics] = useState({
    queryTime: 0,
    renderTime: 0,
    totalItems: 0,
    cacheHits: 0,
    cacheMisses: 0
  });

  const measureQuery = useCallback(async (queryFunction, queryName = 'unknown') => {
    const startTime = performance.now();
    
    try {
      const result = await queryFunction();
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      setMetrics(prev => ({
        ...prev,
        queryTime: duration,
        totalItems: result?.length || 0
      }));
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 Query Performance [${queryName}]: ${duration.toFixed(2)}ms`);
      }
      
      return result;
    } catch (error) {
      console.error(`❌ Query Error [${queryName}]:`, error);
      throw error;
    }
  }, []);

  const recordCacheHit = useCallback(() => {
    setMetrics(prev => ({ ...prev, cacheHits: prev.cacheHits + 1 }));
  }, []);

  const recordCacheMiss = useCallback(() => {
    setMetrics(prev => ({ ...prev, cacheMisses: prev.cacheMisses + 1 }));
  }, []);

  return { metrics, measureQuery, recordCacheHit, recordCacheMiss };
};

// Hook para debounce otimizado
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const OrdersApproval = () => {
  // Estados principais
  const [orders, setOrders] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [approvalForm, setApprovalForm] = useState({
    status: '',
    notes: ''
  });
  
  // Filtros
  const [statusFilter, setStatusFilter] = useState('Pendente');
  const [expositorFilter, setExpositorFilter] = useState('');
  const [eventoFilter, setEventoFilter] = useState('');
  const [dataInicioFilter, setDataInicioFilter] = useState('');
  const [dataFimFilter, setDataFimFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Estados de UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  
  // Cache otimizado
  const [staticDataCache, setStaticDataCache] = useState({
    expositores: null,
    eventos: null,
    lastFetch: null
  });
  
  const [ordersCache, setOrdersCache] = useState({
    data: null,
    lastFetch: null,
    filters: null,
    page: null
  });
  
  // Estados para desconto
  const [editingItemId, setEditingItemId] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [showItemDiscountModal, setShowItemDiscountModal] = useState(false);
  const [orderSummary, setOrderSummary] = useState({
    originalTotal: 0,
    discountTotal: 0,
    finalTotal: 0,
    itemCount: 0,
    itemsWithDiscount: 0
  });
  const [itemDiscountForm, setItemDiscountForm] = useState({
    discountType: 'value',
    discountValue: 0,
    originalPrice: 0,
    finalPrice: 0,
    quantityWithDiscount: 0,
    totalQuantity: 0
  });

  // Estados para abas e feedback
  const [activeTab, setActiveTab] = useState('resumo');
  const [savingItem, setSavingItem] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Hook de performance
  const { metrics, measureQuery, recordCacheHit, recordCacheMiss } = usePerformanceMonitor();

  // Constantes de cache otimizadas
  const CACHE_DURATION = {
    STATIC_DATA: 10 * 60 * 1000, // 10 minutos
    ORDERS: 30 * 1000, // 30 segundos
    SEARCH: 60 * 1000 // 1 minuto
  };

  // Design system colors
  const colors = {
    primary: '#0078D7',
    primaryLight: '#E3F2FD',
    primaryDark: '#005A9E',
    secondary: '#505050',
    success: '#28A745',
    successLight: '#D4EDDA',
    danger: '#DC3545',
    dangerLight: '#F8D7DA',
    warning: '#FFC107',
    warningLight: '#FFF3CD',
    info: '#17A2B8',
    infoLight: '#D1ECF1',
    light: '#F8F9FA',
    dark: '#343A40',
    muted: '#6C757D',
    white: '#FFFFFF',
    border: '#DEE2E6',
    shadow: 'rgba(0, 0, 0, 0.1)'
  };

  // Estilos do modal otimizados
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '1000px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.white,
      borderRadius: '12px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: 'none',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationFillMode: 'both'
    },
    header: {
      background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.border} 100%)`,
      borderBottom: `1px solid ${colors.border}`,
      padding: '1.25rem 1.5rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: colors.white,
      color: colors.muted,
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: `0 2px 5px ${colors.shadow}`
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: colors.dark,
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    tabsContainer: {
      display: 'flex',
      borderBottom: `1px solid ${colors.border}`,
      backgroundColor: colors.light,
      padding: '0 1.5rem',
      flexShrink: 0
    },
    tab: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '1rem 1.5rem',
      cursor: 'pointer',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: colors.muted,
      borderBottom: '3px solid transparent',
      transition: 'all 0.3s ease',
      position: 'relative',
      backgroundColor: 'transparent'
    },
    activeTab: {
      color: colors.primary,
      borderBottomColor: colors.primary,
      backgroundColor: colors.white,
      fontWeight: '600'
    },
    tabContent: {
      padding: '0',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.25rem 1.5rem',
      borderTop: `1px solid ${colors.border}`,
      background: `linear-gradient(135deg, ${colors.light} 0%, ${colors.border} 100%)`,
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '8px',
      fontSize: '0.95rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    cancelButton: {
      border: `1px solid ${colors.border}`,
      background: colors.white,
      color: colors.secondary,
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: colors.primary,
      color: colors.white,
      boxShadow: `0 4px 10px ${colors.shadow}`
    },
    section: {
      margin: '0',
      padding: '1.5rem',
      borderBottom: `1px solid ${colors.border}`
    },
    sectionTitle: {
      fontSize: '1.1rem',
      fontWeight: '600',
      color: colors.dark,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '1rem'
    },
    infoLabel: {
      fontSize: '0.8rem',
      color: colors.muted,
      marginBottom: '0.25rem'
    },
    infoValue: {
      fontSize: '0.95rem',
      fontWeight: '500',
      color: colors.dark
    },
    itemTable: {
      width: '100%',
      borderCollapse: 'collapse',
      background: colors.white
    },
    itemTableHeader: {
      backgroundColor: colors.light,
      color: colors.secondary,
      fontSize: '0.85rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      padding: '1rem',
      textAlign: 'left',
      borderBottom: `1px solid ${colors.border}`,
      position: 'sticky',
      top: 0,
      zIndex: 2
    },
    itemTableCell: {
      padding: '1rem',
      borderBottom: `1px solid ${colors.border}`,
      fontSize: '0.95rem',
      color: colors.dark,
      verticalAlign: 'middle'
    },
    itemActions: {
      display: 'flex',
      gap: '0.5rem',
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    discountButton: {
      padding: '0.5rem',
      borderRadius: '6px',
      border: 'none',
      background: colors.primaryLight,
      color: colors.primary,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },
    discountBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem 0.65rem',
      borderRadius: '50px',
      fontSize: '0.75rem',
      fontWeight: '600',
      backgroundColor: colors.successLight,
      color: colors.success
    },
    priceContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end'
    },
    originalPrice: {
      textDecoration: 'line-through',
      color: colors.muted,
      fontSize: '0.85rem'
    },
    discountedPrice: {
      color: colors.success,
      fontWeight: '600'
    },
    regularPrice: {
      fontWeight: '500'
    },
    statusBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.4rem 0.8rem',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    statusOptionBtn: {
      flex: '1',
      padding: '1rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      borderRadius: '10px',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    orderSummaryCard: {
      backgroundColor: colors.white,
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      padding: '1.5rem',
      boxShadow: `0 4px 10px ${colors.shadow}`,
      marginBottom: '1.5rem'
    },
    summaryRow: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '0.75rem'
    },
    summaryLabel: {
      fontSize: '0.95rem',
      color: colors.secondary
    },
    summaryValue: {
      fontSize: '1rem',
      fontWeight: '600',
      color: colors.dark
    },
    summaryTotal: {
      fontSize: '1.2rem',
      fontWeight: '700',
      color: colors.primary,
      padding: '0.75rem 0',
      borderTop: `1px solid ${colors.border}`,
      marginTop: '0.5rem'
    },
    discountTotal: {
      color: colors.success
    },
    notificationBar: {
      padding: '1rem 1.5rem',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '1rem'
    },
    successNotification: {
      backgroundColor: colors.successLight,
      color: colors.success,
      border: `1px solid ${colors.success}30`
    },
    errorNotification: {
      backgroundColor: colors.dangerLight,
      color: colors.danger,
      border: `1px solid ${colors.danger}30`
    },
    notificationIcon: {
      marginRight: '0.5rem'
    },
    notificationDismiss: {
      background: 'transparent',
      border: 'none',
      color: 'inherit',
      cursor: 'pointer',
      opacity: '0.7'
    },
    itemDiscountModal: {
      width: '100%',
      maxWidth: '700px',
      backgroundColor: colors.white,
      borderRadius: '12px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
      border: 'none',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.3s',
      animationFillMode: 'both',
      maxHeight: '85vh',
      display: 'flex',
      flexDirection: 'column'
    },
    discountForm: {
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      padding: '1.25rem',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem'
    },
    formLabel: {
      fontSize: '0.9rem',
      fontWeight: '600',
      color: colors.secondary
    },
    formInput: {
      padding: '0.75rem 1rem',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      fontSize: '1rem',
      transition: 'all 0.2s',
      width: '100%'
    },
    discountTypeOptions: {
      display: 'flex',
      gap: '1rem',
      marginBottom: '1rem'
    },
    discountTypeOption: {
      flex: '1',
      padding: '0.75rem',
      borderRadius: '8px',
      border: `1px solid ${colors.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      fontSize: '0.9rem',
      fontWeight: '500'
    },
    discountPreview: {
      backgroundColor: colors.infoLight,
      padding: '1.25rem',
      borderRadius: '8px',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem'
    },
    previewRow: {
      display: 'flex',
      justifyContent: 'space-between'
    },
    previewLabel: {
      fontSize: '0.9rem',
      color: colors.secondary
    },
    previewValue: {
      fontSize: '0.95rem',
      fontWeight: '600',
      color: colors.dark
    },
    previewTotal: {
      fontSize: '1.1rem',
      fontWeight: '700',
      color: colors.success
    }
  };

  // Configuração das abas
  const tabs = [
    { id: 'resumo', label: 'Resumo', icon: <FaFileAlt /> },
    { id: 'expositor', label: 'Expositor', icon: <FaBuilding /> },
    { id: 'itens', label: 'Itens', icon: <FaShoppingCart /> },
    { id: 'analise', label: 'Análise', icon: <FaClipboardCheck /> }
  ];

  // Função para mudar de aba
  const changeTab = (tab) => {
    setActiveTab(tab);
  };

  // Função para calcular totais do item com desconto parcial
  const calculateItemTotals = (originalPrice, discountValue, discountType, quantityWithDiscount, totalQuantity) => {
    const discountedPrice = discountType === 'percentage' 
      ? originalPrice * (1 - discountValue / 100) 
      : Math.max(0, originalPrice - discountValue);
    
    const quantityWithoutDiscount = totalQuantity - quantityWithDiscount;
    
    const totalWithDiscount = discountedPrice * quantityWithDiscount;
    const totalWithoutDiscount = originalPrice * quantityWithoutDiscount;
    const finalTotal = totalWithDiscount + totalWithoutDiscount;
    
    const discountAmount = (originalPrice - discountedPrice) * quantityWithDiscount;
    
    return {
      discountedPrice,
      totalWithDiscount,
      totalWithoutDiscount,
      finalTotal,
      discountAmount
    };
  };

  // FUNÇÃO OTIMIZADA: Buscar dados estáticos com cache
  const fetchStaticDataWithCache = useCallback(async () => {
    const now = Date.now();
    
    if (staticDataCache.lastFetch && 
        (now - staticDataCache.lastFetch) < CACHE_DURATION.STATIC_DATA &&
        staticDataCache.expositores && 
        staticDataCache.eventos) {
      
      recordCacheHit();
      return {
        expositores: staticDataCache.expositores,
        eventos: staticDataCache.eventos
      };
    }

    recordCacheMiss();
    
    return await measureQuery(async () => {
      const [expositoresResult, eventosResult] = await Promise.all([
        supabase
          .from('caex_expositores')
          .select('id, nome_empresa, responsavel, email, telefone')
          .eq('ativo', true)
          .order('nome_empresa'),
        supabase
          .from('caex_eventos')
          .select('id, nome, data_inicio, data_fim')
          .eq('ativo', true)
          .order('nome')
      ]);

      if (expositoresResult.error) throw expositoresResult.error;
      if (eventosResult.error) throw eventosResult.error;

      const result = {
        expositores: expositoresResult.data,
        eventos: eventosResult.data
      };

      setStaticDataCache({
        expositores: result.expositores,
        eventos: result.eventos,
        lastFetch: now
      });

      return result;
    }, 'fetchStaticData');
  }, [staticDataCache, measureQuery, recordCacheHit, recordCacheMiss]);

  // FUNÇÃO OTIMIZADA: Buscar pedidos com JOINs e cache
  const fetchOrdersOptimized = useCallback(async (page = 1, forceRefresh = false) => {
    const currentFilters = { 
      statusFilter, 
      expositorFilter, 
      eventoFilter, 
      dataInicioFilter, 
      dataFimFilter,
      searchTerm: debouncedSearchTerm 
    };
    const now = Date.now();
    
    if (!forceRefresh && 
        ordersCache.data && 
        ordersCache.lastFetch &&
        (now - ordersCache.lastFetch) < CACHE_DURATION.ORDERS &&
        ordersCache.page === page &&
        JSON.stringify(ordersCache.filters) === JSON.stringify(currentFilters)) {
      
      recordCacheHit();
      setOrders(ordersCache.data.orders);
      setTotalItems(ordersCache.data.totalItems);
      setTotalPages(ordersCache.data.totalPages);
      return;
    }

    recordCacheMiss();

    try {
      setLoading(true);
      
      return await measureQuery(async () => {
        let query = supabase
          .from('caex_pedidos')
          .select(`
            id,
            expositor_id,
            evento_id,
            valor_total,
            status,
            created_at,
            observacoes,
            caex_expositores!inner (
              id,
              nome_empresa,
              responsavel,
              email,
              telefone
            ),
            caex_eventos!inner (
              id,
              nome,
              data_inicio,
              data_fim
            )
          `, { count: 'exact' });

        if (statusFilter !== 'Todos') {
          query = query.eq('status', mapStatusToDB(statusFilter));
        }

        if (expositorFilter) {
          query = query.eq('caex_expositores.nome_empresa', expositorFilter);
        }

        if (eventoFilter) {
          query = query.eq('caex_eventos.nome', eventoFilter);
        }

        if (dataInicioFilter) {
          query = query.gte('created_at', dataInicioFilter);
        }

        if (dataFimFilter) {
          query = query.lte('created_at', dataFimFilter + 'T23:59:59');
        }

        if (debouncedSearchTerm) {
          const searchTermLower = debouncedSearchTerm.toLowerCase();
          query = query.or(`
            id.eq.${debouncedSearchTerm},
            caex_expositores.nome_empresa.ilike.%${searchTermLower}%,
            caex_expositores.responsavel.ilike.%${searchTermLower}%,
            caex_expositores.email.ilike.%${searchTermLower}%,
            caex_eventos.nome.ilike.%${searchTermLower}%
          `);
        }

        const offset = (page - 1) * itemsPerPage;
        query = query
          .order('created_at', { ascending: false })
          .range(offset, offset + itemsPerPage - 1);

        const { data: pedidosData, error: pedidosError, count } = await query;

        if (pedidosError) throw pedidosError;

        const formattedOrders = pedidosData.map(pedido => ({
          id: pedido.id,
          exhibitorId: pedido.expositor_id,
          exhibitorName: pedido.caex_expositores.nome_empresa,
          eventId: pedido.evento_id,
          eventName: pedido.caex_eventos.nome,
          totalValue: parseFloat(pedido.valor_total) || 0,
          status: mapStatusFromDB(pedido.status),
          createdAt: pedido.created_at,
          notes: pedido.observacoes || '',
          contact: pedido.caex_expositores.responsavel || '',
          email: pedido.caex_expositores.email || '',
          phone: pedido.caex_expositores.telefone || ''
        }));

        const totalItems = count || 0;
        const totalPages = Math.ceil(totalItems / itemsPerPage);

        const cacheData = {
          orders: formattedOrders,
          totalItems,
          totalPages
        };

        setOrdersCache({
          data: cacheData,
          lastFetch: now,
          filters: currentFilters,
          page
        });

        setOrders(formattedOrders);
        setTotalItems(totalItems);
        setTotalPages(totalPages);

        return formattedOrders;
      }, 'fetchOrdersOptimized');

    } catch (error) {
      console.error('❌ Erro ao carregar pedidos:', error);
      setError('Não foi possível carregar a lista de pedidos.');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [
    statusFilter, 
    expositorFilter, 
    eventoFilter, 
    dataInicioFilter, 
    dataFimFilter, 
    debouncedSearchTerm,
    currentPage,
    itemsPerPage,
    ordersCache,
    measureQuery,
    recordCacheHit,
    recordCacheMiss
  ]);

  // FUNÇÃO OTIMIZADA E CORRIGIDA: Buscar itens do pedido
  const fetchOrderItems = useCallback(async (orderId) => {
    try {
      console.log(`🔍 Buscando itens para o pedido ID: ${orderId}`);
      
      return await measureQuery(async () => {
        // PRIMEIRO: Tentar com LEFT JOIN
        let { data, error } = await supabase
          .from('caex_pedidos_itens')
          .select(`
            *,
            caex_produtos (
              id,
              nome,
              descricao,
              categoria_id
            )
          `)
          .eq('pedido_id', orderId);
        
        // SE HOUVER ERRO COM JOIN, TENTAR SEM JOIN
        if (error) {
          console.warn('⚠️ Erro com JOIN, tentando sem JOIN:', error);
          
          const { data: itemsData, error: itemsError } = await supabase
            .from('caex_pedidos_itens')
            .select('*')
            .eq('pedido_id', orderId);
          
          if (itemsError) {
            console.error('❌ Erro na query simples:', itemsError);
            throw itemsError;
          }
          
          data = itemsData.map(item => ({
            ...item,
            caex_produtos: null // Sem dados do produto
          }));
          error = null;
        }
        
        console.log('📊 Dados brutos retornados:', data);
        console.log('📊 Quantidade de registros:', data?.length || 0);
        
        if (!data || data.length === 0) {
          console.log('⚠️ Nenhum item encontrado');
          setOrderItems([]);
          return [];
        }
        
        // Formatação com fallbacks robustos
        const formattedItems = data.map((item, index) => {
          console.log(`📝 Processando item ${index + 1}:`, item);
          
          const quantidade = parseInt(item.quantidade) || 1;
          const precoUnitario = parseFloat(item.preco_unitario) || 0;
          const precoTotal = parseFloat(item.preco_total) || (precoUnitario * quantidade);
          const precoOriginal = parseFloat(item.preco_original) || precoUnitario;
          const desconto = parseFloat(item.desconto) || 0;
          const quantidadeComDesconto = parseInt(item.quantidade_com_desconto) || 0;
          
          // Tratamento robusto do nome do produto
          const produtoNome = item.caex_produtos?.nome || 
                             item.nome_produto || 
                             `Produto #${item.produto_id}`;
          
          const formattedItem = {
            id: item.id,
            productId: item.produto_id,
            name: produtoNome,
            description: item.caex_produtos?.descricao || item.descricao_produto || '',
            category: item.caex_produtos?.categoria_id || '',
            quantity: quantidade,
            price: precoUnitario,
            total: precoTotal,
            originalPrice: precoOriginal,
            discount: desconto,
            discountType: item.tipo_desconto || 'value',
            hasDiscount: quantidadeComDesconto > 0 && desconto > 0,
            quantidadeComDesconto: quantidadeComDesconto
          };
          
          console.log(`✅ Item formatado:`, formattedItem);
          return formattedItem;
        });
        
        console.log(`✅ Total: ${formattedItems.length} itens processados`);
        setOrderItems(formattedItems);
        return formattedItems;
      }, `fetchOrderItems-${orderId}`);

    } catch (error) {
      console.error('❌ Erro ao carregar itens:', error);
      console.error('❌ Stack:', error.stack);
      setErrorMessage(`Falha ao carregar os itens: ${error.message}`);
      setOrderItems([]);
      return [];
    }
  }, [measureQuery]);

  // FUNÇÃO DE DEBUG ALTERNATIVA (usar se ainda houver problemas)
  const debugOrderItems = useCallback(async (orderId) => {
    try {
      console.log(`🔍 [DEBUG] Testando pedido ID: ${orderId}`);
      
      // Testar query mais simples possível
      const { data: simpleData, error: simpleError } = await supabase
        .from('caex_pedidos_itens')
        .select('id, produto_id, quantidade, preco_unitario, preco_total')
        .eq('pedido_id', orderId);
      
      console.log('🧪 [DEBUG] Query simples:', { simpleData, simpleError });
      
      if (simpleError) {
        console.error('❌ [DEBUG] Erro na query simples:', simpleError);
        return [];
      }
      
      return simpleData || [];
      
    } catch (error) {
      console.error('❌ [DEBUG] Erro geral:', error);
      return [];
    }
  }, []);

  // Visualizar detalhes do pedido - OTIMIZADO
  const viewOrderDetails = async (order) => {
    try {
      console.log('👁️ Visualizando pedido:', order.id);
      setLoading(true);
      
      // Limpar estado anterior
      setOrderItems([]);
      setErrorMessage('');
      setSuccessMessage('');
      
      const items = await fetchOrderItems(order.id);
      
      setCurrentOrder({...order, items});
      setApprovalForm({
        status: order.status,
        notes: order.notes
      });
      
      // Resetar estados de edição
      setEditingItemId(null);
      setEditingItem(null);
      setShowItemDiscountModal(false);
      
      // Iniciar com a aba resumo
      setActiveTab('resumo');
      
      setShowDetailsModal(true);
      
      console.log('🏁 Modal aberto com itens:', items);
    } catch (error) {
      console.error('❌ Erro ao carregar detalhes:', error);
      setErrorMessage(`Erro ao carregar detalhes: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Limpar filtros
  const clearAllFilters = useCallback(() => {
    setStatusFilter('Pendente');
    setExpositorFilter('');
    setEventoFilter('');
    setDataInicioFilter('');
    setDataFimFilter('');
    setSearchTerm('');
    setCurrentPage(1);
    
    setOrdersCache({
      data: null,
      lastFetch: null,
      filters: null,
      page: null
    });
  }, []);

  // Mapear status
  const mapStatusFromDB = (dbStatus) => {
    const statusMap = {
      'pendente': 'Pendente',
      'aprovado': 'Aprovado',
      'rejeitado': 'Rejeitado'
    };
    return statusMap[dbStatus] || dbStatus;
  };

  const mapStatusToDB = (uiStatus) => {
    const statusMap = {
      'Pendente': 'pendente',
      'Aprovado': 'aprovado',
      'Rejeitado': 'rejeitado'
    };
    return statusMap[uiStatus] || uiStatus.toLowerCase();
  };

  // Calcular sumário do pedido
  useEffect(() => {
    if (!orderItems?.length) return;
    
    const summary = orderItems.reduce((acc, item) => {
      const quantity = item.quantity || 1;
      const price = parseFloat(item.price) || 0;
      const originalPrice = parseFloat(item.originalPrice) || price;
      const discount = parseFloat(item.discount) || 0;
      const total = parseFloat(item.total) || (price * quantity);
      const quantidadeComDesconto = item.quantidadeComDesconto || 0;
      
      const originalItemTotal = originalPrice * quantity;
      const currentItemTotal = total;
      
      return {
        originalTotal: acc.originalTotal + originalItemTotal,
        finalTotal: acc.finalTotal + currentItemTotal,
        discountTotal: acc.discountTotal + (originalItemTotal - currentItemTotal),
        itemCount: acc.itemCount + 1,
        itemsWithDiscount: acc.itemsWithDiscount + (quantidadeComDesconto > 0 && discount > 0 ? 1 : 0)
      };
    }, {
      originalTotal: 0,
      finalTotal: 0,
      discountTotal: 0,
      itemCount: 0,
      itemsWithDiscount: 0
    });
    
    setOrderSummary(summary);
  }, [orderItems]);

  // Handlers de formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    setApprovalForm({
      ...approvalForm,
      [name]: value
    });
  };

  // Formatação
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR') + ' ' + date.toLocaleTimeString('pt-BR', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // Status info
  const getStatusInfo = useCallback((status) => {
    switch (status) {
      case 'Aprovado':
        return {
          color: colors.success,
          icon: <FaCheckCircle />,
          bgColor: `${colors.success}20`
        };
      case 'Rejeitado':
        return {
          color: colors.danger,
          icon: <FaTimesCircle />,
          bgColor: `${colors.danger}20`
        };
      case 'Pendente':
        return {
          color: colors.warning,
          icon: <FaExclamationCircle />,
          bgColor: `${colors.warning}20`
        };
      default:
        return {
          color: colors.muted,
          icon: null,
          bgColor: `${colors.muted}20`
        };
    }
  }, [colors]);

  // Funções de desconto
  const openItemDiscountModal = (item) => {
    setEditingItem(item);
    setEditingItemId(item.id);
    
    const currentDiscount = item.discount || 0;
    const currentQuantityWithDiscount = item.quantidadeComDesconto || 0;
    
    setItemDiscountForm({
      discountType: item.discountType || 'value',
      discountValue: currentDiscount > 0 ? currentDiscount : '',
      originalPrice: item.originalPrice || item.price,
      finalPrice: item.hasDiscount ? 
        (item.discountType === 'percentage' ? 
          item.price * (1 - item.discount / 100) : 
          item.price - item.discount) : 
        item.price,
      quantityWithDiscount: currentQuantityWithDiscount > 0 ? currentQuantityWithDiscount : '',
      totalQuantity: item.quantity
    });
    setShowItemDiscountModal(true);
  };

  const closeItemDiscountModal = () => {
    setShowItemDiscountModal(false);
    setEditingItemId(null);
    setEditingItem(null);
  };

  const handleDiscountTypeChange = (type) => {
    const { discountValue, originalPrice } = itemDiscountForm;
    
    let newDiscountValue = discountValue;
    if (type === 'percentage' && discountValue > 0) {
      newDiscountValue = Math.min(100, (discountValue * 100 / originalPrice));
    } else if (type === 'value' && discountValue > 0) {
      newDiscountValue = originalPrice * (discountValue / 100);
    }
    
    const newFinalPrice = calculateFinalPrice(originalPrice, newDiscountValue, type);
    
    setItemDiscountForm({
      ...itemDiscountForm,
      discountType: type,
      discountValue: newDiscountValue,
      finalPrice: newFinalPrice
    });
  };

  const handleDiscountValueChange = (e) => {
    let value = e.target.value;
    
    if (value === '') {
      setItemDiscountForm({
        ...itemDiscountForm,
        discountValue: '',
        finalPrice: itemDiscountForm.originalPrice
      });
      return;
    }
    
    const numValue = parseFloat(value) || 0;
    const { discountType, originalPrice } = itemDiscountForm;
    
    const maxValue = discountType === 'percentage' ? 100 : originalPrice;
    const validatedValue = Math.min(maxValue, Math.max(0, numValue));
    
    const newFinalPrice = calculateFinalPrice(originalPrice, validatedValue, discountType);
    
    setItemDiscountForm({
      ...itemDiscountForm,
      discountValue: validatedValue,
      finalPrice: newFinalPrice
    });
  };

  const handleQuantityWithDiscountChange = (e) => {
    let value = e.target.value;
    
    if (value === '') {
      setItemDiscountForm({
        ...itemDiscountForm,
        quantityWithDiscount: ''
      });
      return;
    }
    
    const numValue = parseInt(value) || 0;
    const maxQuantity = itemDiscountForm.totalQuantity;
    const validatedValue = Math.min(maxQuantity, Math.max(0, numValue));
    
    setItemDiscountForm({
      ...itemDiscountForm,
      quantityWithDiscount: validatedValue
    });
  };

  const calculateFinalPrice = (originalPrice, discountValue, discountType) => {
    if (discountValue <= 0) return originalPrice;
    
    return discountType === 'percentage' 
      ? originalPrice * (1 - discountValue / 100) 
      : Math.max(0, originalPrice - discountValue);
  };

  // Salvar desconto
  const saveItemDiscount = async () => {
    if (!editingItem || !currentOrder) return;
    
    setSavingItem(true);
    
    try {
      const discountValue = itemDiscountForm.discountValue === '' ? 0 : parseFloat(itemDiscountForm.discountValue) || 0;
      const quantityWithDiscount = itemDiscountForm.quantityWithDiscount === '' ? 0 : parseInt(itemDiscountForm.quantityWithDiscount) || 0;
      
      const { discountType, originalPrice, totalQuantity } = itemDiscountForm;
      
      const calculations = calculateItemTotals(
        originalPrice,
        discountValue,
        discountType,
        quantityWithDiscount,
        totalQuantity
      );
      
      const updateData = {
        preco_original: originalPrice,
        preco_unitario: originalPrice,
        preco_total: calculations.finalTotal,
        tipo_desconto: discountType,
        desconto: discountValue,
        quantidade_com_desconto: quantityWithDiscount,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('caex_pedidos_itens')
        .update(updateData)
        .eq('id', editingItem.id);
      
      if (error) throw error;
      
      const updatedItems = orderItems.map(item => 
        item.id === editingItem.id 
          ? { 
              ...item, 
              price: originalPrice,
              total: calculations.finalTotal,
              originalPrice: originalPrice,
              discount: discountValue,
              discountType: discountType,
              hasDiscount: quantityWithDiscount > 0 && discountValue > 0,
              quantidadeComDesconto: quantityWithDiscount,
              discountAmount: calculations.discountAmount
            } 
          : item
      );
      
      setOrderItems(updatedItems);
      
      const newTotal = updatedItems.reduce((sum, item) => sum + parseFloat(item.total), 0);
      
      await supabase
        .from('caex_pedidos')
        .update({ 
          valor_total: newTotal,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrder.id);
      
      setCurrentOrder({
        ...currentOrder,
        totalValue: newTotal
      });
      
      setSuccessMessage(
        quantityWithDiscount > 0 ? 
          `Desconto aplicado a ${quantityWithDiscount} de ${totalQuantity} unidades!` :
          'Desconto removido com sucesso!'
      );
      closeItemDiscountModal();
    } catch (error) {
      console.error('❌ Erro ao aplicar desconto:', error);
      setErrorMessage(`Não foi possível aplicar o desconto: ${error.message}`);
    } finally {
      setSavingItem(false);
    }
  };

  // Remover desconto
  const removeItemDiscount = async (itemId) => {
    const item = orderItems.find(i => i.id === itemId);
    if (!item || !currentOrder) return;
    
    try {
      const updateData = {
        preco_unitario: item.originalPrice,
        preco_total: item.originalPrice * item.quantity,
        tipo_desconto: null,
        desconto: 0,
        quantidade_com_desconto: 0,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('caex_pedidos_itens')
        .update(updateData)
        .eq('id', itemId);
      
      if (error) throw error;
      
      const updatedItems = orderItems.map(item => 
        item.id === itemId 
          ? { 
              ...item, 
              price: item.originalPrice,
              total: item.originalPrice * item.quantity,
              discount: 0,
              discountType: 'value',
              hasDiscount: false,
              quantidadeComDesconto: 0
            } 
          : item
      );
      
      setOrderItems(updatedItems);
      
      const newTotal = updatedItems.reduce((sum, item) => sum + item.total, 0);
      
      await supabase
        .from('caex_pedidos')
        .update({ 
          valor_total: newTotal,
          updated_at: new Date().toISOString()
        })
        .eq('id', currentOrder.id);
      
      setCurrentOrder({
        ...currentOrder,
        totalValue: newTotal
      });
      
      setSuccessMessage('Desconto removido com sucesso!');
    } catch (error) {
      console.error('❌ Erro ao remover desconto:', error);
      setErrorMessage('Não foi possível remover o desconto. Tente novamente.');
    }
  };

  // Atualizar status do pedido
  const updateOrderStatus = async () => {
    if (!currentOrder) return;
    
    try {
      setSavingOrder(true);
      
      const updateData = {
        status: mapStatusToDB(approvalForm.status),
        observacoes: approvalForm.notes,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('caex_pedidos')
        .update(updateData)
        .eq('id', currentOrder.id);
      
      if (error) throw error;
      
      setOrders(orders.map(order => 
        order.id === currentOrder.id 
          ? { 
              ...order, 
              status: approvalForm.status, 
              notes: approvalForm.notes
            } 
          : order
      ));
      
      setOrdersCache({
        data: null,
        lastFetch: null,
        filters: null,
        page: null
      });
      
      setSuccessMessage(`Status do pedido atualizado para ${approvalForm.status}!`);
      
      setTimeout(() => {
        setShowDetailsModal(false);
        fetchOrdersOptimized(currentPage, true);
      }, 1500);
    } catch (error) {
      console.error('❌ Erro ao atualizar status:', error);
      setErrorMessage('Não foi possível atualizar o status do pedido. Tente novamente.');
    } finally {
      setSavingOrder(false);
    }
  };

  // Renderizar conteúdo da aba
  const renderTabContent = () => {
    switch (activeTab) {
      case 'resumo':
        return (
          <div style={modalStyles.section}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <h3 style={modalStyles.sectionTitle}>
                <FaFileAlt style={{color: colors.muted}} />
                Resumo do Pedido
              </h3>
              <span style={{ 
                ...modalStyles.statusBadge,
                backgroundColor: getStatusInfo(currentOrder.status).bgColor,
                color: getStatusInfo(currentOrder.status).color
              }}>
                {getStatusInfo(currentOrder.status).icon}
                {currentOrder.status}
              </span>
            </div>
            
            <div style={modalStyles.orderSummaryCard}>
              <div style={modalStyles.summaryRow}>
                <span style={modalStyles.summaryLabel}>Expositor:</span>
                <span style={modalStyles.summaryValue}>{currentOrder.exhibitorName}</span>
              </div>
              <div style={modalStyles.summaryRow}>
                <span style={modalStyles.summaryLabel}>Evento:</span>
                <span style={modalStyles.summaryValue}>{currentOrder.eventName}</span>
              </div>
              <div style={modalStyles.summaryRow}>
                <span style={modalStyles.summaryLabel}>Data do pedido:</span>
                <span style={modalStyles.summaryValue}>{formatDate(currentOrder.createdAt)}</span>
              </div>
              <div style={modalStyles.summaryRow}>
                <span style={modalStyles.summaryLabel}>Itens no pedido:</span>
                <span style={modalStyles.summaryValue}>{orderSummary.itemCount}</span>
              </div>
              {orderSummary.itemsWithDiscount > 0 && (
                <div style={modalStyles.summaryRow}>
                  <span style={modalStyles.summaryLabel}>Itens com desconto:</span>
                  <span style={modalStyles.summaryValue}>{orderSummary.itemsWithDiscount}</span>
                </div>
              )}
              {orderSummary.discountTotal > 0 && (
                <div style={modalStyles.summaryRow}>
                  <span style={modalStyles.summaryLabel}>Desconto total:</span>
                  <span style={{...modalStyles.summaryValue, ...modalStyles.discountTotal}}>
                    {formatCurrency(orderSummary.discountTotal)}
                  </span>
                </div>
              )}
              <div style={modalStyles.summaryTotal}>
                <div style={modalStyles.summaryRow}>
                  <span>Valor total do pedido:</span>
                  {orderSummary.discountTotal > 0 ? (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.9rem', textDecoration: 'line-through', color: colors.muted }}>
                        {formatCurrency(orderSummary.originalTotal)}
                      </div>
                      <div style={{ color: colors.success }}>
                        {formatCurrency(orderSummary.finalTotal)}
                      </div>
                    </div>
                  ) : (
                    <span>{formatCurrency(orderSummary.finalTotal)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'expositor':
        return (
          <div style={modalStyles.section}>
            <h3 style={modalStyles.sectionTitle}>
              <FaBuilding style={{color: colors.muted}} />
              Informações do Expositor
            </h3>
            <div style={modalStyles.grid}>
              <div>
                <p style={modalStyles.infoLabel}>Responsável</p>
                <p style={modalStyles.infoValue}>{currentOrder.contact || 'Não informado'}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Email</p>
                <p style={modalStyles.infoValue}>{currentOrder.email || 'Não informado'}</p>
              </div>
              <div>
                <p style={modalStyles.infoLabel}>Telefone</p>
                <p style={modalStyles.infoValue}>{currentOrder.phone || 'Não informado'}</p>
              </div>
            </div>
          </div>
        );

      case 'itens':
        console.log('🔍 Renderizando aba itens');
        console.log('📊 Estado orderItems:', orderItems);
        console.log('📊 orderItems.length:', orderItems?.length);

        return (
          <div style={modalStyles.section}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
              <h3 style={modalStyles.sectionTitle}>
                <FaShoppingCart style={{color: colors.muted}} />
                Itens do Pedido
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {process.env.NODE_ENV === 'development' && (
                  <>
                    <button
                      onClick={() => debugOrderItems(currentOrder.id)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        backgroundColor: colors.info,
                        color: colors.white,
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.8rem',
                        cursor: 'pointer'
                      }}
                    >
                      🧪 Debug Query
                    </button>
                    <div style={{fontSize: '0.8rem', color: colors.muted}}>
                      Debug: {orderItems?.length || 0} itens
                    </div>
                  </>
                )}
              </div>
            </div>
            
            <div className="table-container">
              <table style={modalStyles.itemTable}>
                <thead>
                  <tr>
                    <th style={modalStyles.itemTableHeader}>Item</th>
                    <th style={{...modalStyles.itemTableHeader, width: '100px', textAlign: 'center'}}>Quantidade</th>
                    <th style={{...modalStyles.itemTableHeader, width: '130px', textAlign: 'right'}}>Preço Unit.</th>
                    <th style={{...modalStyles.itemTableHeader, width: '130px', textAlign: 'right'}}>Subtotal</th>
                    {approvalForm.status === 'Pendente' && (
                      <th style={{...modalStyles.itemTableHeader, width: '100px', textAlign: 'right'}}>Ações</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {orderItems && Array.isArray(orderItems) && orderItems.length > 0 ? (
                    orderItems.map((item, index) => {
                      console.log(`🔍 Renderizando item ${index}:`, item);
                      return (
                        <tr key={item.id || index}>
                          <td style={modalStyles.itemTableCell}>
                            <div>
                              <div style={{fontWeight: '500'}}>
                                {item.name || `Item #${item.id}`}
                              </div>
                              {item.description && (
                                <small style={{color: colors.muted}}>{item.description}</small>
                              )}
                              {process.env.NODE_ENV === 'development' && (
                                <div style={{fontSize: '0.7rem', color: colors.info, marginTop: '0.25rem'}}>
                                  ID: {item.id} | ProdID: {item.productId}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{...modalStyles.itemTableCell, textAlign: 'center'}}>
                            <div>
                              <span style={{fontWeight: '500'}}>{item.quantity || 0}</span>
                              {item.hasDiscount && item.quantidadeComDesconto > 0 && (
                                <div style={{
                                  fontSize: '0.75rem', 
                                  color: colors.success, 
                                  marginTop: '0.25rem',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center'
                                }}>
                                  <span style={{
                                    backgroundColor: colors.successLight,
                                    padding: '0.2rem 0.4rem',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem'
                                  }}>
                                    <FaTag size={8} />
                                    {item.quantidadeComDesconto} com desconto
                                  </span>
                                  {(item.quantity - item.quantidadeComDesconto) > 0 && (
                                    <span style={{
                                      color: colors.muted,
                                      marginTop: '0.15rem'
                                    }}>
                                      {item.quantity - item.quantidadeComDesconto} preço normal
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td style={{...modalStyles.itemTableCell, textAlign: 'right'}}>
                            <div style={modalStyles.priceContainer}>
                              {item.hasDiscount && item.quantidadeComDesconto > 0 ? (
                                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end'}}>
                                  <span style={{...modalStyles.regularPrice, fontSize: '0.9rem'}}>
                                    {formatCurrency(item.originalPrice || 0)}
                                  </span>
                                  
                                  <div style={{
                                    fontSize: '0.8rem',
                                    color: colors.success,
                                    marginTop: '0.25rem',
                                    textAlign: 'right'
                                  }}>
                                    <div>
                                      {item.quantidadeComDesconto}x {formatCurrency(
                                        item.discountType === 'percentage' 
                                          ? (item.originalPrice || 0) * (1 - (item.discount || 0) / 100)
                                          : (item.originalPrice || 0) - (item.discount || 0)
                                      )}
                                    </div>
                                    {(item.quantity - item.quantidadeComDesconto) > 0 && (
                                      <div style={{color: colors.muted}}>
                                        {item.quantity - item.quantidadeComDesconto}x {formatCurrency(item.originalPrice || 0)}
                                      </div>
                                    )}
                                  </div>
                                  
                                  <span style={{
                                    ...modalStyles.discountBadge,
                                    marginTop: '0.5rem',
                                    fontSize: '0.7rem'
                                  }}>
                                    <FaTag size={8} />
                                    {item.discountType === 'percentage' 
                                      ? `${item.discount}%` 
                                      : formatCurrency(item.discount)} off
                                  </span>
                                </div>
                              ) : (
                                <span style={modalStyles.regularPrice}>
                                  {formatCurrency(item.originalPrice || 0)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td style={{...modalStyles.itemTableCell, textAlign: 'right'}}>
                            <div style={{textAlign: 'right'}}>
                              <div style={{fontWeight: '600', fontSize: '1rem'}}>
                                {formatCurrency(item.total || 0)}
                              </div>
                              
                              {item.hasDiscount && item.quantidadeComDesconto > 0 && (
                                <div style={{
                                  fontSize: '0.75rem',
                                  color: colors.success,
                                  marginTop: '0.25rem'
                                }}>
                                  Economia: {formatCurrency(
                                    (item.discountType === 'percentage' 
                                      ? (item.originalPrice || 0) * ((item.discount || 0) / 100)
                                      : (item.discount || 0)) * item.quantidadeComDesconto
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          {approvalForm.status === 'Pendente' && (
                            <td style={{...modalStyles.itemTableCell, textAlign: 'right'}}>
                              <div style={modalStyles.itemActions}>
                                {item.hasDiscount && item.quantidadeComDesconto > 0 ? (
                                  <button 
                                    onClick={() => removeItemDiscount(item.id)}
                                    style={{
                                      ...modalStyles.discountButton,
                                      backgroundColor: colors.dangerLight,
                                      color: colors.danger
                                    }}
                                    title="Remover desconto"
                                  >
                                    <FaUndo size={14} />
                                  </button>
                                ) : null}
                                <button 
                                  onClick={() => openItemDiscountModal(item)}
                                  style={{
                                    padding: '0.5rem 0.75rem',
                                    borderRadius: '6px',
                                    backgroundColor: colors.primary,
                                    color: colors.white,
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                                    transition: 'all 0.2s ease'
                                  }}
                                  title={item.hasDiscount ? "Editar desconto" : "Aplicar desconto"}
                                >
                                  <FaTag size={12} />
                                  {item.hasDiscount ? "Editar" : "Desconto"}
                                </button>
                              </div>
                            </td>
                          )}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td 
                        colSpan={approvalForm.status === 'Pendente' ? 5 : 4}
                        style={{...modalStyles.itemTableCell, textAlign: 'center', padding: '2rem'}}
                      >
                        <div style={{color: colors.muted, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem'}}>
                          <FaInfoCircle size={32} />
                          <div>
                            <p style={{fontWeight: '500', marginBottom: '0.5rem'}}>Nenhum item encontrado</p>
                            <p style={{fontSize: '0.9rem'}}>Este pedido não possui itens registrados.</p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  
                  {orderItems && orderItems.length > 0 && (
                    <>
                      <tr>
                        <td 
                          colSpan={approvalForm.status === 'Pendente' ? 3 : 2} 
                          style={{
                            ...modalStyles.itemTableCell, 
                            textAlign: 'right',
                            fontWeight: '600'
                          }}
                        >
                          Total:
                        </td>
                        <td 
                          style={{
                            ...modalStyles.itemTableCell, 
                            textAlign: 'right',
                            fontWeight: '700',
                            fontSize: '1.1rem',
                            color: orderSummary.discountTotal > 0 ? colors.success : colors.dark
                          }}
                        >
                          {formatCurrency(orderSummary.finalTotal)}
                        </td>
                        {approvalForm.status === 'Pendente' && <td style={modalStyles.itemTableCell}></td>}
                      </tr>
                      
                      {orderSummary.discountTotal > 0 && (
                        <tr>
                          <td 
                            colSpan={approvalForm.status === 'Pendente' ? 3 : 2} 
                            style={{
                              ...modalStyles.itemTableCell, 
                              textAlign: 'right',
                              color: colors.success,
                              fontStyle: 'italic'
                            }}
                          >
                            Economia total:
                          </td>
                          <td 
                            style={{
                              ...modalStyles.itemTableCell, 
                              textAlign: 'right',
                              color: colors.success,
                              fontWeight: '500'
                            }}
                          >
                            {formatCurrency(orderSummary.discountTotal)}
                          </td>
                          {approvalForm.status === 'Pendente' && <td style={modalStyles.itemTableCell}></td>}
                        </tr>
                      )}
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'analise':
        return (
          <div style={modalStyles.section}>
            <h3 style={modalStyles.sectionTitle}>
              <FaClipboardCheck style={{color: colors.muted}} />
              Análise do Pedido
            </h3>
            <div className="form-group">
              <label className="form-label">
                Status do Pedido
              </label>
              <div style={{display: 'flex', gap: '1rem'}}>
                {['Pendente', 'Aprovado', 'Rejeitado'].map(status => {
                  const statusInfo = getStatusInfo(status);
                  const isActive = approvalForm.status === status;
                  return (
                    <div 
                      key={status}
                      style={{
                        ...modalStyles.statusOptionBtn,
                        backgroundColor: isActive ? statusInfo.bgColor : colors.white,
                        borderColor: isActive ? statusInfo.color : colors.border,
                        border: isActive ? `2px solid ${statusInfo.color}` : `1px solid ${colors.border}`,
                        boxShadow: isActive ? `0 5px 15px ${colors.shadow}` : 'none'
                      }}
                      onClick={() => setApprovalForm({...approvalForm, status})}
                    >
                      {statusInfo.icon}
                      <span style={{ color: statusInfo.color, fontWeight: '600' }}>{status}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">
                Observações
              </label>
              <textarea
                name="notes"
                value={approvalForm.notes}
                onChange={handleChange}
                rows="3"
                className="form-input"
                placeholder="Adicione observações sobre a aprovação ou rejeição do pedido..."
              ></textarea>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Carregar dados iniciais
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        
        await fetchStaticDataWithCache();
        await fetchOrdersOptimized(1);
        
      } catch (error) {
        console.error('❌ Erro ao carregar dados iniciais:', error);
        setError('Falha ao carregar os dados necessários.');
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();

    // CSS animações
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(20px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .form-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        font-size: 0.95rem;
        color: #495057;
        transition: all 0.2s ease;
      }
      
      .form-input:focus {
        outline: none;
        border-color: #0078D7;
        box-shadow: 0 0 0 3px rgba(0, 120, 215, 0.15);
      }
      
      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        color: #505050;
      }
      
      .form-group {
        margin-bottom: 1.25rem;
      }
      
      .input-group {
        display: flex;
        width: 100%;
      }
      
      .input-group-text {
        display: flex;
        align-items: center;
        padding: 0.75rem 1rem;
        font-size: 0.95rem;
        font-weight: 500;
        color: #495057;
        text-align: center;
        white-space: nowrap;
        background-color: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 8px 0 0 8px;
        border-right: none;
      }
      
      .input-group .form-input {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        flex: 1 1 auto;
      }
      
      .table-container {
        position: relative;
        overflow: auto;
        max-height: 400px;
        border-radius: 8px;
        border: 1px solid #dee2e6;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Recarregar pedidos quando filtros mudarem
  useEffect(() => {
    if (!loading) {
      fetchOrdersOptimized(currentPage);
    }
  }, [
    statusFilter, 
    expositorFilter, 
    eventoFilter, 
    dataInicioFilter, 
    dataFimFilter, 
    debouncedSearchTerm,
    currentPage,
    fetchOrdersOptimized
  ]);

  // Impedir scroll quando modal aberto
  useEffect(() => {
    if (showDetailsModal || showItemDiscountModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showDetailsModal, showItemDiscountModal]);

  // Auto-clear mensagens
  useEffect(() => {
    let timer;
    if (successMessage) {
      timer = setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Loading state
  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaClipboardCheck className="dashboard-title-icon" />
                Aprovação de Pedidos
              </h1>
              <p className="dashboard-subtitle">
                Carregando dados...
              </p>
            </div>
          </div>
          <div className="text-center p-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
            <p className="mt-3">Carregando pedidos...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaClipboardCheck className="dashboard-title-icon" />
                Aprovação de Pedidos
              </h1>
              <p className="dashboard-subtitle">
                Erro ao carregar dados
              </p>
            </div>
          </div>
          <div className="alert alert-danger">
            <h5>Erro ao carregar dados</h5>
            <p>{error}</p>
            <button 
              className="btn btn-outline-primary mt-3"
              onClick={() => window.location.reload()}
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaClipboardCheck className="dashboard-title-icon" />
              Aprovação de Pedidos
            </h1>
            <p className="dashboard-subtitle">
              Análise e aprovação de pedidos dos expositores
            </p>
          </div>
          
          {process.env.NODE_ENV === 'development' && (
            <div style={{
              display: 'flex',
              gap: '1rem',
              fontSize: '0.8rem',
              color: colors.muted
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FaTachometerAlt />
                <span>Query: {metrics.queryTime.toFixed(0)}ms</span>
              </div>
              <div>Cache: {metrics.cacheHits}H/{metrics.cacheMisses}M</div>
              <div>Itens: {totalItems}</div>
            </div>
          )}
        </div>

        {/* Filtros */}
        <div style={{
          backgroundColor: colors.white,
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <FaFilter style={{ marginRight: '0.5rem', color: colors.primary }} />
            <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Filtros</h3>
          </div>
          
          <div style={{
            position: 'relative',
            marginBottom: '1rem'
          }}>
            <FaSearch style={{
              position: 'absolute',
              left: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: colors.muted
            }} />
            <input
              type="text"
              placeholder="Buscar por ID, expositor, evento, contato..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem 1rem 0.75rem 2.5rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '8px',
                fontSize: '0.95rem'
              }}
            />
          </div>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem'
          }}>
            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
                Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              >
                <option value="Todos">Todos</option>
                <option value="Pendente">Pendente</option>
                <option value="Aprovado">Aprovado</option>
                <option value="Rejeitado">Rejeitado</option>
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
                Expositor
              </label>
              <select
                value={expositorFilter}
                onChange={(e) => setExpositorFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              >
                <option value="">Todos os expositores</option>
                {staticDataCache.expositores?.map(expositor => (
                  <option key={expositor.id} value={expositor.nome_empresa}>
                    {expositor.nome_empresa}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
                Evento
              </label>
              <select
                value={eventoFilter}
                onChange={(e) => setEventoFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              >
                <option value="">Todos os eventos</option>
                {staticDataCache.eventos?.map(evento => (
                  <option key={evento.id} value={evento.nome}>
                    {evento.nome}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
                Data Início
              </label>
              <input
                type="date"
                value={dataInicioFilter}
                onChange={(e) => setDataInicioFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              />
            </div>

            <div>
              <label style={{ fontSize: '0.9rem', fontWeight: '500', marginBottom: '0.5rem', display: 'block' }}>
                Data Fim
              </label>
              <input
                type="date"
                value={dataFimFilter}
                onChange={(e) => setDataFimFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'end' }}>
              <button
                onClick={clearAllFilters}
                style={{
                  padding: '0.75rem 1rem',
                  backgroundColor: colors.light,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <FaUndo />
                Limpar
              </button>
            </div>
          </div>
        </div>

        {/* Tabela */}
        <div className="modern-table-card" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <div className="modern-card-header">
            <h5 className="modern-card-title">
              <FaClipboardCheck className="card-title-icon" />
              Lista de Pedidos {totalItems > 0 && `(${totalItems})`}
            </h5>
          </div>
          <div className="modern-card-body">
            <div className="modern-table-container">
              {orders.length === 0 ? (
                <div className="text-center p-5">
                  <FaClipboardCheck size={48} className="text-muted mb-3" />
                  <h5>Nenhum pedido encontrado</h5>
                  <p className="text-muted">
                    {statusFilter === 'Todos' ? 
                      'Não há pedidos registrados no sistema.' : 
                      `Não há pedidos com status "${statusFilter}".`}
                  </p>
                </div>
              ) : (
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Expositor</th>
                      <th>Evento</th>
                      <th>Data</th>
                      <th>Valor Total</th>
                      <th>Status</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => {
                      const statusInfo = getStatusInfo(order.status);
                      return (
                        <tr key={order.id}>
                          <td className="order-id">#{order.id}</td>
                          <td className="event-name">{order.exhibitorName}</td>
                          <td className="event-name">{order.eventName}</td>
                          <td className="order-date">{formatDate(order.createdAt)}</td>
                          <td className="order-value">{formatCurrency(order.totalValue)}</td>
                          <td>
                            <span style={{ 
                              ...modalStyles.statusBadge,
                              backgroundColor: statusInfo.bgColor,
                              color: statusInfo.color
                            }}>
                              {statusInfo.icon}
                              {order.status}
                            </span>
                          </td>
                          <td className="text-right">
                            <div className="flex justify-end">
                              <button 
                                onClick={() => viewOrderDetails(order)}
                                style={{
                                  ...modalStyles.footerButton,
                                  ...modalStyles.cancelButton
                                }}
                              >
                                <FaEye />
                                Ver detalhes
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Paginação */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginTop: '1.5rem'
          }}>
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                backgroundColor: currentPage === 1 ? colors.light : colors.white,
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
              }}
            >
              Anterior
            </button>
            
            <span style={{ fontSize: '0.9rem', color: colors.secondary }}>
              Página {currentPage} de {totalPages} ({totalItems} itens)
            </span>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: `1px solid ${colors.border}`,
                backgroundColor: currentPage === totalPages ? colors.light : colors.white,
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
              }}
            >
              Próxima
            </button>
          </div>
        )}

        {/* Modal de detalhes */}
        {showDetailsModal && currentOrder && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  <FaClipboardCheck style={{color: colors.muted}} />
                  Detalhes do Pedido #{currentOrder.id}
                </h2>
                <button 
                  onClick={() => setShowDetailsModal(false)}
                  style={modalStyles.closeButton}
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              <div style={modalStyles.tabsContainer}>
                {tabs.map((tab) => (
                  <div
                    key={tab.id}
                    style={{
                      ...modalStyles.tab,
                      ...(activeTab === tab.id ? modalStyles.activeTab : {})
                    }}
                    onClick={() => changeTab(tab.id)}
                  >
                    {tab.icon}
                    {tab.label}
                  </div>
                ))}
              </div>
              
              <div style={modalStyles.tabContent}>
                {successMessage && (
                  <div style={{...modalStyles.notificationBar, ...modalStyles.successNotification}}>
                    <div>
                      <FaCheckCircle style={modalStyles.notificationIcon} />
                      {successMessage}
                    </div>
                    <button 
                      style={modalStyles.notificationDismiss}
                      onClick={() => setSuccessMessage('')}
                    >
                      ✕
                    </button>
                  </div>
                )}
                
                {errorMessage && (
                  <div style={{...modalStyles.notificationBar, ...modalStyles.errorNotification}}>
                    <div>
                      <FaTimesCircle style={modalStyles.notificationIcon} />
                      {errorMessage}
                    </div>
                    <button 
                      style={modalStyles.notificationDismiss}
                      onClick={() => setErrorMessage('')}
                    >
                      ✕
                    </button>
                  </div>
                )}
              
                {renderTabContent()}
              </div>
              
              <div style={modalStyles.footer}>
                <button
                  type="button"
                  onClick={() => setShowDetailsModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  disabled={savingOrder}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={updateOrderStatus}
                  style={{...modalStyles.footerButton, ...modalStyles.submitButton}}
                  disabled={savingOrder}
                >
                  {savingOrder ? (
                    <>
                      <FaSyncAlt className="fa-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <FaSave />
                      Atualizar Status
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Modal de desconto */}
        {showItemDiscountModal && editingItem && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.itemDiscountModal}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  <FaTag style={{color: colors.primary}} />
                  Aplicar Desconto - {editingItem.name}
                </h2>
                <button 
                  onClick={closeItemDiscountModal}
                  style={modalStyles.closeButton}
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>
              
              <div style={modalStyles.discountForm}>
                <div style={{display: 'flex', gap: '1.5rem', marginBottom: '1rem'}}>
                  <div style={{flex: '2'}}>
                    <div style={{...modalStyles.formGroup, marginTop: '1rem', marginBottom: '0.75rem'}}>
                      <label style={modalStyles.formLabel}>Preço unitário original</label>
                      <div style={{
                        padding: '0.75rem 1rem',
                        backgroundColor: colors.light,
                        borderRadius: '8px',
                        fontWeight: '500'
                      }}>
                        {formatCurrency(itemDiscountForm.originalPrice)}
                      </div>
                    </div>
                    
                    <div style={{...modalStyles.formGroup, marginBottom: '0.75rem'}}>
                      <label style={modalStyles.formLabel}>Quantidade com desconto</label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <FaTag />
                        </span>
                        <input
                          type="number"
                          value={itemDiscountForm.quantityWithDiscount === '' ? '' : itemDiscountForm.quantityWithDiscount}
                          onChange={handleQuantityWithDiscountChange}
                          className="form-input"
                          min="0"
                          max={itemDiscountForm.totalQuantity}
                          step="1"
                          placeholder="0"
                        />
                        <span className="input-group-text">
                          de {itemDiscountForm.totalQuantity}
                        </span>
                      </div>
                    </div>
                    
                    <div style={{...modalStyles.formGroup, marginBottom: '0.75rem'}}>
                      <label style={modalStyles.formLabel}>Tipo de Desconto</label>
                      <div style={modalStyles.discountTypeOptions}>
                        <div 
                          style={{
                            ...modalStyles.discountTypeOption,
                            backgroundColor: itemDiscountForm.discountType === 'value' ? colors.primary : colors.white,
                            color: itemDiscountForm.discountType === 'value' ? colors.white : colors.secondary
                          }}
                          onClick={() => handleDiscountTypeChange('value')}
                        >
                          <FaDollarSign />
                          Valor Fixo
                        </div>
                        <div 
                          style={{
                            ...modalStyles.discountTypeOption,
                            backgroundColor: itemDiscountForm.discountType === 'percentage' ? colors.primary : colors.white,
                            color: itemDiscountForm.discountType === 'percentage' ? colors.white : colors.secondary
                          }}
                          onClick={() => handleDiscountTypeChange('percentage')}
                        >
                          <FaPercent />
                          Porcentagem
                        </div>
                      </div>
                    </div>
                    
                    <div style={{...modalStyles.formGroup, marginBottom: '0.75rem'}}>
                      <label style={modalStyles.formLabel}>
                        {itemDiscountForm.discountType === 'percentage' ? 'Desconto (%)' : 'Desconto (R$)'}
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          {itemDiscountForm.discountType === 'percentage' ? <FaPercent /> : <FaDollarSign />}
                        </span>
                        <input
                          type="number"
                          value={itemDiscountForm.discountValue === '' ? '' : itemDiscountForm.discountValue}
                          onChange={handleDiscountValueChange}
                          className="form-input"
                          min="0"
                          max={itemDiscountForm.discountType === 'percentage' ? 100 : itemDiscountForm.originalPrice}
                          step={itemDiscountForm.discountType === 'percentage' ? 1 : 0.01}
                          placeholder="0"
                        />
                      </div>
                      <small style={{fontSize: '0.8rem', color: colors.muted, marginTop: '0.5rem', display: 'block'}}>
                        {itemDiscountForm.discountType === 'percentage' 
                          ? 'Informe uma porcentagem de 0 a 100%' 
                          : `O desconto máximo permitido é ${formatCurrency(itemDiscountForm.originalPrice)}`}
                      </small>
                    </div>
                  </div>
                  
                  <div style={{flex: '1.5', display: 'flex', flexDirection: 'column'}}>
                    <div style={{...modalStyles.discountPreview, marginTop: '0.5rem', height: '100%'}}>
                      <h4 style={{fontSize: '1rem', fontWeight: '600', marginBottom: '0.75rem', color: colors.info}}>
                        <FaInfoCircle className="me-2" />
                        Preview do Desconto
                      </h4>
                      
                      {(() => {
                        // Tratar valores vazios
                        const discountValue = itemDiscountForm.discountValue === '' ? 0 : parseFloat(itemDiscountForm.discountValue) || 0;
                        const quantityWithDiscount = itemDiscountForm.quantityWithDiscount === '' ? 0 : parseInt(itemDiscountForm.quantityWithDiscount) || 0;
                        
                        const calculations = calculateItemTotals(
                          itemDiscountForm.originalPrice,
                          discountValue,
                          itemDiscountForm.discountType,
                          quantityWithDiscount,
                          itemDiscountForm.totalQuantity
                        );
                        
                        return (
                          <>
                            <div style={modalStyles.previewRow}>
                              <span style={modalStyles.previewLabel}>Quantidade total:</span>
                              <span style={modalStyles.previewValue}>{itemDiscountForm.totalQuantity} unidades</span>
                            </div>
                            
                            <div style={modalStyles.previewRow}>
                              <span style={modalStyles.previewLabel}>Com desconto:</span>
                              <span style={modalStyles.previewValue}>{quantityWithDiscount} unidades</span>
                            </div>
                            
                            <div style={modalStyles.previewRow}>
                              <span style={modalStyles.previewLabel}>Sem desconto:</span>
                              <span style={modalStyles.previewValue}>{itemDiscountForm.totalQuantity - quantityWithDiscount} unidades</span>
                            </div>
                            
                            <div style={{height: '1px', backgroundColor: colors.border, margin: '0.75rem 0'}}></div>
                            
                            <div style={modalStyles.previewRow}>
                              <span style={modalStyles.previewLabel}>Preço original:</span>
                              <span style={modalStyles.previewValue}>{formatCurrency(itemDiscountForm.originalPrice)}</span>
                            </div>
                            
                            {quantityWithDiscount > 0 && discountValue > 0 && (
                              <>
                                <div style={modalStyles.previewRow}>
                                  <span style={modalStyles.previewLabel}>Preço com desconto:</span>
                                  <span style={{...modalStyles.previewValue, color: colors.success}}>
                                    {formatCurrency(calculations.discountedPrice)}
                                  </span>
                                </div>
                                
                                <div style={modalStyles.previewRow}>
                                  <span style={modalStyles.previewLabel}>Valor do desconto total:</span>
                                  <span style={{...modalStyles.previewValue, color: colors.success}}>
                                    {formatCurrency(calculations.discountAmount)}
                                  </span>
                                </div>
                              </>
                            )}
                            
                            <div style={{height: '1px', backgroundColor: colors.border, margin: '0.75rem 0'}}></div>
                            
                            {quantityWithDiscount > 0 && (
                              <div style={modalStyles.previewRow}>
                                <span style={modalStyles.previewLabel}>Subtotal com desconto:</span>
                                <span style={{...modalStyles.previewValue, color: colors.success}}>
                                  {formatCurrency(calculations.totalWithDiscount)}
                                </span>
                              </div>
                            )}
                            
                            {(itemDiscountForm.totalQuantity - quantityWithDiscount) > 0 && (
                              <div style={modalStyles.previewRow}>
                                <span style={modalStyles.previewLabel}>Subtotal sem desconto:</span>
                                <span style={modalStyles.previewValue}>
                                  {formatCurrency(calculations.totalWithoutDiscount)}
                                </span>
                              </div>
                            )}
                            
                            <div style={modalStyles.previewRow}>
                              <span style={modalStyles.previewLabel}>Total final:</span>
                              <span style={modalStyles.previewTotal}>
                                {formatCurrency(calculations.finalTotal)}
                              </span>
                            </div>
                          </>
                        );
                      })()}
                      
                      <div style={{
                        marginTop: 'auto', 
                        backgroundColor: colors.successLight,
                        padding: '1rem',
                        borderRadius: '8px',
                        marginTop: '1.5rem'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          marginBottom: '0.5rem',
                          color: colors.success,
                          fontWeight: '600'
                        }}>
                          <FaInfoCircle />
                          <span>Informação</span>
                        </div>
                        <p style={{fontSize: '0.9rem', color: colors.dark, margin: 0}}>
                          {(() => {
                            const currentQuantityWithDiscount = itemDiscountForm.quantityWithDiscount === '' ? 0 : parseInt(itemDiscountForm.quantityWithDiscount) || 0;
                            return currentQuantityWithDiscount > 0 ? 
                              `O desconto será aplicado em ${currentQuantityWithDiscount} de ${itemDiscountForm.totalQuantity} unidades. ` +
                              `As ${itemDiscountForm.totalQuantity - currentQuantityWithDiscount} unidades restantes serão cobradas pelo preço original.` :
                              'Selecione a quantidade que receberá desconto.';
                          })()}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div style={{...modalStyles.footer, padding: '1rem', borderTop: `1px solid ${colors.border}`}}>
                <button
                  type="button"
                  onClick={closeItemDiscountModal}
                  style={{
                    ...modalStyles.footerButton, 
                    ...modalStyles.cancelButton,
                    padding: '0.6rem 1.2rem'
                  }}
                  disabled={savingItem}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={saveItemDiscount}
                  style={{
                    ...modalStyles.footerButton, 
                    ...modalStyles.submitButton,
                    padding: '0.6rem 1.2rem'
                  }}
                  disabled={savingItem}
                >
                  {savingItem ? (
                    <>
                      <FaSyncAlt className="fa-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <FaTag />
                      Aplicar Desconto
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrdersApproval;